import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, FileText, Type, Mail, CheckSquare, Calendar,
  Settings, ChevronDown, GitBranch, Slack, Database, Bell, ArrowRight,
  TrendingUp, Users, Eye, Send, Plus,
} from 'lucide-react';

/**
 * HeroProductPreview — Cycles between 3 real-product UI mockups
 * (Dashboard, Form Builder, Workflow Automation) inside a browser chrome.
 * Built with the same Tailwind/Shadcn tokens used in the live app.
 */

const SLIDES = ['dashboard', 'builder', 'workflow'];
const CYCLE_MS = 5000;

const BrowserChrome = ({ children }) => (
  <div className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-indigo-900/10 overflow-hidden">
    {/* Top bar */}
    <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/80">
      <div className="flex gap-1.5">
        <span className="w-3 h-3 rounded-full bg-red-400" />
        <span className="w-3 h-3 rounded-full bg-amber-400" />
        <span className="w-3 h-3 rounded-full bg-green-400" />
      </div>
      <div className="mx-auto bg-white border border-slate-200 rounded-md px-3 py-1 text-[11px] text-slate-500 font-mono flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        formlio.app
      </div>
    </div>
    {/* Slide stage */}
    <div className="relative h-[420px] bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-hidden">
      {children}
    </div>
  </div>
);

// ─── Slide 1: Dashboard preview ────────────────────────────────────────────
const DashboardSlide = () => {
  const stats = [
    { label: 'Total Forms',       value: '24',     icon: FileText, color: 'text-indigo-600 bg-indigo-50' },
    { label: 'Submissions',       value: '1,284',  icon: Send,     color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Views',             value: '4,910',  icon: Eye,      color: 'text-blue-600 bg-blue-50' },
    { label: 'Conversion',        value: '26.1%',  icon: TrendingUp, color: 'text-violet-600 bg-violet-50' },
  ];
  const forms = [
    { name: 'Lead Capture — Q2',    submissions: 312, color: 'bg-indigo-500'  },
    { name: 'Customer Onboarding',  submissions: 184, color: 'bg-emerald-500' },
    { name: 'Field Service Report', submissions: 97,  color: 'bg-amber-500'   },
  ];
  return (
    <div className="p-6 h-full flex gap-5">
      {/* Side nav */}
      <div className="hidden sm:flex w-12 shrink-0 flex-col items-center gap-3 py-2">
        <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center"><LayoutDashboard className="w-4 h-4 text-white" /></div>
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><FileText className="w-4 h-4 text-slate-500" /></div>
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Users className="w-4 h-4 text-slate-500" /></div>
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Settings className="w-4 h-4 text-slate-500" /></div>
      </div>
      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-base font-semibold text-slate-900">Dashboard</div>
            <div className="text-xs text-slate-500">Welcome back, Alex</div>
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 text-white text-xs font-medium px-3 py-1.5">
            <Plus className="w-3 h-3" /> New form
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
                className="rounded-lg border border-slate-100 bg-white p-3"
              >
                <div className={`w-7 h-7 rounded-md ${s.color} flex items-center justify-center mb-2`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="text-[11px] text-slate-500">{s.label}</div>
                <div className="text-base font-semibold text-slate-900">{s.value}</div>
              </motion.div>
            );
          })}
        </div>
        <div className="rounded-lg border border-slate-100 bg-white p-3 flex-1">
          <div className="text-xs font-semibold text-slate-700 mb-2">Recent forms</div>
          <div className="divide-y divide-slate-100">
            {forms.map((f, i) => (
              <motion.div
                key={f.name}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + i * 0.1 }}
                className="flex items-center justify-between py-2 text-xs"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-1.5 h-1.5 rounded-full ${f.color}`} />
                  <span className="font-medium text-slate-700 truncate">{f.name}</span>
                </div>
                <span className="text-slate-500 shrink-0">{f.submissions} subs</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Slide 2: Form Builder UI ──────────────────────────────────────────────
const BuilderSlide = () => {
  const elements = [
    { icon: Type,        label: 'Short text' },
    { icon: Mail,        label: 'Email' },
    { icon: CheckSquare, label: 'Checkbox' },
    { icon: Calendar,    label: 'Date' },
  ];
  return (
    <div className="p-4 h-full flex gap-3 text-[11px]">
      {/* Left: dark elements panel */}
      <div className="hidden sm:flex w-32 shrink-0 flex-col gap-2 rounded-lg bg-slate-900 p-2.5">
        <div className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Elements</div>
        {elements.map((el, i) => {
          const Icon = el.icon;
          return (
            <motion.div
              key={el.label}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.05 * i }}
              className="flex items-center gap-2 px-2 py-1.5 rounded text-slate-200 hover:bg-slate-700/60"
            >
              <Icon className="w-3 h-3 text-slate-400" />
              <span>{el.label}</span>
            </motion.div>
          );
        })}
      </div>
      {/* Center: canvas */}
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <div className="rounded-lg border border-slate-100 bg-white px-3 py-2">
          <div className="text-[10px] text-slate-500 mb-1">Full name *</div>
          <div className="h-7 rounded border border-slate-200 bg-slate-50" />
        </div>
        <div className="rounded-lg border border-slate-100 bg-white px-3 py-2">
          <div className="text-[10px] text-slate-500 mb-1">Email *</div>
          <div className="h-7 rounded border border-slate-200 bg-slate-50" />
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
          className="rounded-lg border-2 border-indigo-500 bg-indigo-50/40 px-3 py-2 shadow-sm"
        >
          <div className="text-[10px] text-indigo-700 font-medium mb-1">Phone</div>
          <div className="h-7 rounded border border-indigo-200 bg-white" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-slate-400 text-center"
        >
          Drop here ↓
        </motion.div>
      </div>
      {/* Right: dark properties panel */}
      <div className="hidden md:flex w-36 shrink-0 flex-col gap-2 rounded-lg bg-slate-900 p-2.5">
        <div className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Field properties</div>
        <div>
          <div className="text-slate-400 mb-1">Label</div>
          <div className="rounded bg-slate-700/60 h-6 px-2 flex items-center text-slate-200">Phone</div>
        </div>
        <div className="flex items-center justify-between text-slate-300">
          <span>Required</span>
          <span className="w-7 h-3.5 rounded-full bg-indigo-500 relative">
            <span className="absolute right-0.5 top-0.5 w-2.5 h-2.5 rounded-full bg-white" />
          </span>
        </div>
        <div className="flex items-center justify-between text-slate-300">
          <span>Read only</span>
          <span className="w-7 h-3.5 rounded-full bg-slate-600 relative">
            <span className="absolute left-0.5 top-0.5 w-2.5 h-2.5 rounded-full bg-slate-300" />
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Slide 3: Workflow Automation ──────────────────────────────────────────
const WorkflowSlide = () => {
  const steps = [
    { label: 'Form submitted',  sub: 'Lead Capture',   icon: FileText, color: 'bg-indigo-500',  ring: 'ring-indigo-200' },
    { label: 'If: Country = US',sub: 'Conditional',    icon: GitBranch, color: 'bg-amber-500',   ring: 'ring-amber-200' },
    { label: 'Notify on Slack', sub: '#sales-leads',   icon: Slack,    color: 'bg-emerald-500', ring: 'ring-emerald-200' },
    { label: 'Save to CRM',     sub: 'Append row',     icon: Database, color: 'bg-violet-500',  ring: 'ring-violet-200' },
    { label: 'Email customer',  sub: 'Auto-reply',     icon: Mail,     color: 'bg-rose-500',    ring: 'ring-rose-200' },
  ];
  return (
    <div className="p-6 h-full flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Workflow · Lead Capture</div>
          <div className="text-[11px] text-slate-500">Triggered on every submission</div>
        </div>
        <div className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Live
        </div>
      </div>

      <div className="flex-1 flex flex-col items-stretch justify-center gap-2.5 py-1">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <React.Fragment key={s.label}>
              <motion.div
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.18 }}
                className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2 shadow-sm"
              >
                <div className={`w-8 h-8 rounded-lg ${s.color} text-white flex items-center justify-center ring-4 ${s.ring}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-800">{s.label}</div>
                  <div className="text-[11px] text-slate-500">{s.sub}</div>
                </div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4 + i * 0.18 }}
                  className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px] font-bold"
                >
                  ✓
                </motion.div>
              </motion.div>
              {i < steps.length - 1 && (
                <motion.div
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ delay: 0.25 + i * 0.18, duration: 0.25 }}
                  style={{ originY: 0 }}
                  className="ml-7 w-px h-3 bg-gradient-to-b from-slate-300 to-slate-200"
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const SLIDE_COMPONENTS = {
  dashboard: DashboardSlide,
  builder:   BuilderSlide,
  workflow:  WorkflowSlide,
};

const SLIDE_LABELS = {
  dashboard: 'Dashboard',
  builder:   'Form Builder',
  workflow:  'Automation',
};

export const HeroProductPreview = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex(i => (i + 1) % SLIDES.length), CYCLE_MS);
    return () => clearInterval(t);
  }, []);

  const current = SLIDES[index];
  const SlideComponent = SLIDE_COMPONENTS[current];

  return (
    <div className="relative" data-testid="hero-product-preview">
      {/* Soft glow behind the chrome */}
      <div className="absolute -inset-6 bg-gradient-to-tr from-indigo-200/40 via-violet-200/30 to-transparent blur-2xl -z-10" />

      <BrowserChrome>
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, scale: 0.985, y: 8 }}
            animate={{ opacity: 1, scale: 1,     y: 0 }}
            exit={{    opacity: 0, scale: 1.015, y: -6 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
            className="absolute inset-0"
          >
            <SlideComponent />
          </motion.div>
        </AnimatePresence>
      </BrowserChrome>

      {/* Slide indicator pills */}
      <div className="mt-4 flex items-center justify-center gap-2" role="tablist">
        {SLIDES.map((s, i) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={i === index}
            onClick={() => setIndex(i)}
            data-testid={`hero-preview-pill-${s}`}
            className={`group flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
              i === index
                ? 'bg-slate-900 text-white shadow-md'
                : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${i === index ? 'bg-emerald-400' : 'bg-slate-300'}`} />
            {SLIDE_LABELS[s]}
          </button>
        ))}
      </div>
    </div>
  );
};

export default HeroProductPreview;
